import React, { useCallback, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { PREQUAL } from "@/constants/testIds";
import { PREQUAL_STEPS, INITIAL_DATA } from "@/lib/prequalConfig";
import { STEP_COMPONENTS } from "@/components/prequal/steps";
import { ProgressBar, NavButtons, SuccessScreen } from "@/components/prequal/atoms";

const PrequalForm = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL_DATA);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = useCallback(
    (k, v) => setData((d) => ({ ...d, [k]: v })),
    []
  );

  const currentStep = PREQUAL_STEPS[step];
  const StepComponent = STEP_COMPONENTS[currentStep.id];
  const canGoNext = useMemo(
    () => currentStep.isValid(data),
    [currentStep, data]
  );

  const handleNext = useCallback(() => {
    if (currentStep.isValid(data)) {
      setStep((s) => Math.min(PREQUAL_STEPS.length - 1, s + 1));
    }
  }, [currentStep, data]);

  const handlePrev = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        edad: data.edad ? Number(data.edad) : undefined,
        dependientes:
          data.dependientes !== "" ? Number(String(data.dependientes).replace("+", "")) : undefined,
        categoria_recomendada: data.proteccion,
        origen: "precalificacion",
      };
      await createLead(payload);
      setDone(true);
      toast.success("¡Gracias! Recibimos tu información.");
    } catch (e) {
      toast.error("Hubo un problema al enviar. Inténtalo nuevamente.");
    } finally {
      setSubmitting(false);
    }
  }, [data]);

  if (done) return <SuccessScreen proteccion={data.proteccion} />;

  return (
    <div className="bg-white border border-razmek p-8 lg:p-12">
      <ProgressBar
        step={step}
        total={PREQUAL_STEPS.length}
        currentLabel={currentStep.label}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          data-testid={PREQUAL.step(step)}
        >
          <StepComponent data={data} update={update} />
        </motion.div>
      </AnimatePresence>

      <NavButtons
        step={step}
        totalSteps={PREQUAL_STEPS.length}
        canGoNext={canGoNext}
        submitting={submitting}
        onPrev={handlePrev}
        onNext={handleNext}
        onSubmit={submit}
      />
    </div>
  );
};

export default PrequalForm;
